<template>
  <ProjectNodeLayout>
    <template #content>
      <PendingBillingPeriodChange
        class="tw-mb-8"
        :period="node.targetBillingPeriod"
        :paid-until="paidUntilDate"
      />

      <Section title="Hardware details">
        <SpecSheet>
          <SpecSheetItem v-if="node.datacenter" label="Region">
            <DatacenterBadge :datacenter="node.datacenter" />
          </SpecSheetItem>

          <SpecSheetItem
            v-if="node.cloudProvider"
            label="Region"
            :value="node.cloudProvider.region"
          />

          <SpecSheetItem v-if="node.image" label="OS" :value="node.image.name" />

          <SpecSheetItem label="IP Addresses">
            {{ node.networkInterfaces.map((iface) => iface.ipAddresses).join(', ') }}
          </SpecSheetItem>

          <SpecSheetItem label="Hostname" :value="node.fqdn" />

          <SpecSheetItem label="Flavour" :value="node.flavour?.name" />

          <SpecSheetItem v-if="node.flavour?.cpu" label="CPU" :value="node.flavour.cpu" />
          <SpecSheetItem v-if="node.flavour?.memory" label="Memory" :value="node.flavour.memory" />
          <SpecSheetItem v-if="node.flavour?.disk" label="Disc" :value="node.flavour.disk" />

          <SpecSheetItem v-if="node.cloudProvider != null" label="Cloud provider">
            <ExternalLink :url="node.permalink" class="tw-cursor-pointer">
              {{ cloudProviderType }}
            </ExternalLink>
          </SpecSheetItem>

          <SpecSheetItem
            v-if="node.cloudProviderResourceId"
            label="Cloud Resource ID"
            :value="node.cloudProviderResourceId"
          />

          <SpecSheetItem
            v-if="node.flavour?.network"
            label="Network"
            :value="node.flavour?.network"
          />
        </SpecSheet>
      </Section>

      <template v-if="node.cloudProvider == null">
        <Section title="Health checks">
          <MonitoringStateDetail :state="node.monitoringState" />
        </Section>

        <Section title="Billing information">
          <SpecSheet>
            <SpecSheetItem
              v-if="node.billingPeriod === BillingPeriod.HOURLY"
              label="Period"
              value="Hourly"
            />
            <template v-else-if="node.billingPeriod === BillingPeriod.MONTHLY">
              <SpecSheetItem label="Period" value="Monthly" />
              <SpecSheetItem label="Paid until" :value="paidUntilFormatted" />
            </template>
          </SpecSheet>
        </Section>
      </template>

      <Section title="Manage tags">
        <p class="tw-mb-4">
          All tags also provided by the
          <ExternalLink :url="`${$config.docs.url}/guides/meta-data/`">metadata</ExternalLink>
          service endpoints.
        </p>

        <form @submit.prevent="saveTags()">
          <p class="tw-mb-2 tw-font-bold">Tags</p>
          <div
            class="mt-4 tw-mb-4 tw-flex tw-gap-4 tw-p-0"
            v-for="(tag, i) in tags"
            :key="`tag_${tag.id}`"
          >
            <GpTextInput
              v-model="tag.key"
              :input-id="`tag__key__${i}`"
              name="key"
              placeholder="Key of the tag"
            />
            <span :id="`basic-addon__${i}`">/</span>
            <GpTextInput
              v-model="tag.value"
              :input-id="`tag__value__${i}`"
              name="value"
              placeholder="Value of the tag"
            />
            <GpBtn
              btn-type="danger"
              size="sm"
              type="button"
              :disabled="tag.key === '' || tag.value === ''"
              @click="deleteTag(i)"
              label="Delete"
            />
          </div>

          <GpBtn
            class="tw-mt-8 tw-w-full"
            :disabled="!tagsReady"
            type="submit"
            :icon="['fas', 'fa-save']"
            label="Save"
          />
        </form>
      </Section>
    </template>
  </ProjectNodeLayout>
</template>
<script lang="ts">
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import ProjectNodeLayout from '@panel/layouts/Layout.Project.Node.vue';
import { convertToDate } from 'gpcore-apikit/lib/date';
import { useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import DatacenterBadge from 'gpcore-uikit/components/Component.Badge.Datacenter.vue';
import Section from 'gpcore-uikit/components/Component.Section.vue';
import MonitoringStateDetail from 'gpcore-uikit/components/Component.Server.MonitoringStateDetail.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { GpBtn, GpTextInput } from '@gportal/uikit';
import PendingBillingPeriodChange from '@panel/components/node/PendingBillingPeriodChange.vue';
import SpecSheet from 'gpcore-uikit/components/new/specs/SpecSheet.vue';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import SpecSheetItem from 'gpcore-uikit/components/new/specs/SpecSheetItem.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import getEnumLabel from '@lib/getEnumLabel';
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from 'vue';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { create } from '@bufbuild/protobuf';
import { NodeSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';

interface Tag {
  key: string;
  value: string;
  id?: string;
}

export default defineComponent({
  components: {
    PendingBillingPeriodChange,
    MonitoringStateDetail,
    ProjectNodeLayout,
    Section,
    DatacenterBadge,
    SpecSheet,
    SpecSheetItem,
    ExternalLink,
    GpBtn,
    GpTextInput,
  },

  setup() {
    useProjectViewTitle('Node overview');

    const nodeStore = useNodeStore();
    const projectStore = useProjectStore();
    const { addNotification } = useNotifications();

    const node = computed(() => nodeStore.node);
    const project = computed(() => projectStore.project);

    const tags = ref<Array<Tag>>([]);

    const filled = computed(
      () => tags.value.filter((x: Tag) => x.value === '' || x.key === '').length === 0,
    );
    const tagsReady = computed(() => {
      if (tags.value.length === 0) {
        return false;
      }

      let ready = true;
      tags.value.forEach((t: Tag) => {
        if (t.value === '' && t.key !== '') {
          ready = false;
        }

        if (t.key === '' && t.value !== '') {
          ready = false;
        }
      });

      return ready;
    });

    const paidUntilDate = computed(() =>
      node.value != null ? convertToDate(node.value.paidUntilAt) : undefined,
    );

    const paidUntilFormatted = computed(() =>
      node.value ? paidUntilDate.value.toUTCString() : undefined,
    );

    const addTagLine = () => {
      tags.value.push({ key: '', value: '', id: new Date().getTime().toString() });
    };

    const reloadTags = () => {
      tags.value = new Array<Tag>();
      if (node.value) {
        tags.value = Object.entries(node.value.tags).map(([key, value]) => ({ key, value }));
      }

      addTagLine();
    };

    const deleteTag = (i: number) => {
      tags.value.splice(i, 1);
      if (tags.value.length === 0) {
        addTagLine();
      }
    };

    const saveTags = () => {
      const newNode = create(NodeSchema);
      newNode.id = node.value.id;
      tags.value.forEach((t: Tag) => {
        if (t.key !== '' && t.value !== '') {
          newNode.tags[t.key] = t.value;
        }
      });

      nodeStore
        .updateNode(newNode)
        .then(() => {
          addNotification({
            message: 'The node tags has been saved.',
            type: 'success',
          });
        })
        .catch((error: ServiceError) => {
          addNotification({
            message: error.message,
            type: 'danger',
          });
        });
    };

    const nodeWatchHandler = watch(
      () => nodeStore.node,
      () => {
        reloadTags();
      },
    );

    const filledWatchHandler = watch(
      () => filled.value,
      () => {
        if (filled.value) {
          addTagLine();
        }
      },
    );

    const cloudProviderType = computed(() =>
      getEnumLabel(CloudProviderType, node.value?.cloudProvider?.type),
    );

    onMounted(() => {
      reloadTags();
    });

    onUnmounted(() => {
      nodeWatchHandler();
      filledWatchHandler();
    });

    return {
      node,
      paidUntilFormatted,
      paidUntilDate,
      project,
      tags,
      filled,
      tagsReady,
      saveTags,
      deleteTag,
      BillingPeriod,
      cloudProviderType,
    };
  },
});
</script>
